import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import apiClient from '@src/api/clients';
import {
  createInvoice,
  getClientChannelBreakdown,
  getParentChannelBreakdown,
  getClientSpendPreview,
  retrieveInvoice,
  sendInvoice,
  updateInvoice,
  uploadInvoiceAttachment,
} from '@src/api/invoices';
import { DEFAULT_ACH_INSTRUCTIONS } from '@src/constants/invoicing';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const getDefaultPeriod = () => {
  const end = dayjs().subtract(1, 'month').endOf('month');
  const start = end.startOf('month');
  return {
    start: start.format('YYYY-MM-DD'),
    end: end.format('YYYY-MM-DD'),
  };
};

const resolveBillingCycleKey = (value) => {
  if (!value) {
    return '';
  }
  const text = String(value).trim().toLowerCase();
  if (text.includes('bi') && text.includes('week')) {
    return 'bi_weekly';
  }
  if (text.includes('month')) {
    return 'monthly';
  }
  return text.replace(/\s+/g, '_');
};

const getPreviousBiWeeklyPeriod = () => {
  const today = dayjs();
  if (today.date() <= 15) {
    const previousMonth = today.subtract(1, 'month');
    const start = previousMonth.date(16);
    const end = previousMonth.endOf('month');
    return {
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    };
  }
  const start = today.date(1);
  const end = today.date(15);
  return {
    start: start.format('YYYY-MM-DD'),
    end: end.format('YYYY-MM-DD'),
  };
};

const getPeriodForBillingCycle = (cycle) => {
  const key = resolveBillingCycleKey(cycle);
  if (key === 'bi_weekly') {
    return getPreviousBiWeeklyPeriod();
  }
  return getDefaultPeriod();
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return '—';
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatDateTime = (value) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—');

const createEmptyLineItem = () => ({ line_item_type: 'custom', description: '', amount: '' });

const toInputString = (value) => (value === null || value === undefined ? '' : String(value));

const toFeePercentageNumber = (value, { isFraction = false } = {}) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric) || !Number.isFinite(numeric)) {
    return 0;
  }
  return isFraction ? numeric * 100 : numeric;
};

const formatFeePercentageDisplay = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric) || !Number.isFinite(numeric)) {
    return '';
  }
  if (numeric === 0) {
    return '0%';
  }
  if (numeric % 1 === 0) {
    return `${numeric}%`;
  }
  return `${numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
};

const formatSpendFundingLabel = (value) => {
  if (!value) {
    return '—';
  }
  if (value === 'client_funds_spend') {
    return 'Client funded ad spend';
  }
  if (value === 'guru_funds_spend') {
    return 'Lead Gurus funded ad spend';
  }
  return value;
};

const formatAmountInput = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric) || !Number.isFinite(numeric)) {
    return '';
  }
  return numeric.toFixed(2);
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric) || !Number.isFinite(numeric)) {
    return null;
  }
  return numeric;
};

const MEDIA_SPEND_FEES_LABEL = 'Media Spend Fees';
const MONTHLY_MIN_TOP_UP_LABEL = 'Monthly Minimum Fee Top-Up';

const isMediaSpendFeeItem = (item) =>
  ['media spend fee', 'media spend fees'].includes(
    String(item?.description || '').trim().toLowerCase().replace(/\s+/g, ' ')
  );

const isMonthlyTopUpItem = (item) =>
  String(item?.description || '').trim().toLowerCase().replace(/\s+/g, ' ') ===
  'monthly minimum fee top-up';

const isAdSpendLineItem = (item) =>
  String(item?.description || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .includes('ad spend');

const buildBreakdownLineItems = ({
  entries,
  total,
  periodStart,
  periodEnd,
  spendFunding,
  feePercentageValue,
}) => {
  const formattedStart = periodStart ? dayjs(periodStart).format('MMM D, YYYY') : '';
  const formattedEnd = periodEnd ? dayjs(periodEnd).format('MMM D, YYYY') : '';
  let rangeLabel = '';
  if (formattedStart && formattedEnd) {
    rangeLabel = `${formattedStart} – ${formattedEnd}`;
  } else if (formattedStart || formattedEnd) {
    rangeLabel = formattedStart || formattedEnd;
  }

  const includeAdSpend = spendFunding !== 'client_funds_spend';
  const baseItems = includeAdSpend
    ? (entries || [])
        .filter((entry) => Number(entry?.spend || 0) > 0)
        .map((entry) => {
          const amount = Number(entry.spend || 0);
          const descriptionRange = rangeLabel ? ` (${rangeLabel})` : '';
          return {
            line_item_type: 'ad_spend',
            description: `${entry.channel || 'Unassigned'} Ad Spend${descriptionRange}`,
            amount: formatAmountInput(amount),
          };
        })
    : [];

  const feePercentage = feePercentageValue || 0;
  const feeRate = Number.isFinite(feePercentage) ? feePercentage / 100 : 0;
  const totalNumber = Number(total ?? 0);
  const feeAmount = totalNumber * feeRate;
  const feeLineItem =
    totalNumber > 0
      ? {
          line_item_type: 'media_fee',
          description: 'Media Spend Fees',
          amount: formatAmountInput(feeAmount),
        }
      : null;

  return feeLineItem ? [...baseItems, feeLineItem] : baseItems;
};

const recalculateMediaSpendFeeLineItems = (
  items,
  feePercentageValue,
  { spendFunding = '', totalSpend = 0 } = {}
) => {
  const safeItems = Array.isArray(items) ? items : [];
  const existingFeeItem = safeItems.find((item) => isMediaSpendFeeItem(item));
  const baseItems = safeItems.filter((item) => !isMediaSpendFeeItem(item));
  const isClientFundedSpend = spendFunding === 'client_funds_spend';
  const spendTotal = isClientFundedSpend
    ? Number(totalSpend || 0)
    : baseItems.filter((item) => isAdSpendLineItem(item)).reduce((sum, item) => {
        const amount = Number(item?.amount || 0);
        return Number.isFinite(amount) ? sum + amount : sum;
      }, 0);
  const feePct = Number(feePercentageValue || 0);
  const feeRate = Number.isFinite(feePct) ? feePct / 100 : 0;
  if (spendTotal <= 0 || feeRate <= 0) {
    return baseItems.length ? baseItems : [createEmptyLineItem()];
  }
  const feeAmount = spendTotal * feeRate;
  const feeLine = {
    ...(existingFeeItem || {}),
    line_item_type: 'media_fee',
    description: MEDIA_SPEND_FEES_LABEL,
    amount: formatAmountInput(feeAmount),
  };
  return [...baseItems, feeLine];
};

const parseBillingTermsDays = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const normalized = String(value).toLowerCase();
  if (normalized.includes('receipt')) {
    return 0;
  }
  const match = normalized.match(/\d+/);
  if (!match) {
    return null;
  }
  const days = Number(match[0]);
  return Number.isFinite(days) ? days : null;
};

const calculateDueDate = (issueDate, billingTerms) => {
  if (!issueDate) {
    return '';
  }
  const days = parseBillingTermsDays(billingTerms);
  if (days === null) {
    return '';
  }
  return dayjs(issueDate).add(days, 'day').format('YYYY-MM-DD');
};

export default function InvoiceFormPage() {
  const { invoiceId } = useParams();
  const isEdit = Boolean(invoiceId);
  const navigate = useNavigate();
  const { error: showErrorMessage, success: showSuccessMessage } = useMessage();

  const defaultPeriod = getDefaultPeriod();
  const [formData, setFormData] = useState({
    billing_scope: 'client',
    client: '',
    included_client_ids: [],
    period_start: defaultPeriod.start,
    period_end: defaultPeriod.end,
    issue_date: dayjs().format('YYYY-MM-DD'),
    due_date: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    notes: '',
    external_notes: '',
    needs_amount_confirmation: true,
  });
  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [sendAfterSave, setSendAfterSave] = useState(false);
  const [sendNote, setSendNote] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [dueDateDirty, setDueDateDirty] = useState(false);
  const [clientBillingInfo, setClientBillingInfo] = useState({
    billingCycle: '',
    billingTerms: '',
    feePercentageDisplay: '',
    feePercentageValue: 0,
    spendFunding: '',
  });
  const [invoiceBillingSnapshot, setInvoiceBillingSnapshot] = useState({
    billingCycle: '',
    billingTerms: '',
    feePercentageDisplay: '',
    feePercentageValue: 0,
    spendFunding: '',
  });
  const [achInstructions, setAchInstructions] = useState(DEFAULT_ACH_INSTRUCTIONS);
  const [channelBreakdown, setChannelBreakdown] = useState([]);
  const [channelBreakdownTotal, setChannelBreakdownTotal] = useState(0);
  const [parentClientOptions, setParentClientOptions] = useState([]);
  const [parentCompanyInfo, setParentCompanyInfo] = useState({ id: null, name: '' });
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [refreshingLineItems, setRefreshingLineItems] = useState(false);
  const [lineItemsDirty, setLineItemsDirty] = useState(false);
  const lineItemsDirtyRef = useRef(false);
  const [monthlyMinimumPreview, setMonthlyMinimumPreview] = useState({
    required: false,
    amount: 1000,
    feesToDate: 0,
    gap: 0,
  });

  const getInvoiceSaveErrorMessage = (error) => {
    const data = error?.response?.data;
    if (!data) {
      return null;
    }
    if (typeof data === 'string') {
      return data;
    }
    if (Array.isArray(data)) {
      return data.filter(Boolean).join(' ');
    }
    if (data.detail) {
      return data.detail;
    }
    const messages = [];
    Object.entries(data).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      const normalized = Array.isArray(value)
        ? value.filter(Boolean).join(' ')
        : typeof value === 'string'
          ? value
          : JSON.stringify(value);
      if (!normalized) {
        return;
      }
      if (key === 'non_field_errors') {
        messages.push(normalized);
      } else {
        messages.push(`${key}: ${normalized}`);
      }
    });
    if (messages.length) {
      return messages.join(' ');
    }
    return null;
  };

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientSearch,
    addOption: addClientOption,
    findOptionByValue,
  } = useClientAutocomplete();

  const markLineItemsDirty = useCallback((dirty) => {
    lineItemsDirtyRef.current = dirty;
    setLineItemsDirty(dirty);
  }, []);

  const selectedClientOption = useMemo(
    () => findOptionByValue(formData.client),
    [findOptionByValue, formData.client]
  );

  const loadInvoice = useCallback(() => {
    if (!isEdit || !invoiceId) {
      return;
    }
    setLoading(true);
    retrieveInvoice(invoiceId)
      .then((res) => {
        const invoice = res.data;
        setFormData({
          billing_scope: invoice.billing_scope || 'client',
          client: invoice.client,
          included_client_ids:
            invoice.included_clients_snapshot?.map((entry) => Number(entry?.id)).filter(Boolean) ||
            [],
          period_start: invoice.period_start,
          period_end: invoice.period_end,
          issue_date: invoice.issue_date || dayjs().format('YYYY-MM-DD'),
          due_date: invoice.due_date || '',
          notes: invoice.notes || '',
          external_notes: invoice.external_notes || '',
          needs_amount_confirmation: Boolean(invoice.needs_amount_confirmation),
        });
        approvalResetToastRef.current = Boolean(invoice.needs_amount_confirmation);
        setAchInstructions(invoice.ach_instructions_snapshot || DEFAULT_ACH_INSTRUCTIONS);
        const invoiceFeeValue = toFeePercentageNumber(invoice.fee_percentage_snapshot);
        const invoiceBillingDetails = {
          billingCycle: invoice.billing_cycle_snapshot || '',
          billingTerms: invoice.billing_terms_snapshot || '',
          feePercentageDisplay: formatFeePercentageDisplay(invoiceFeeValue),
          feePercentageValue: invoiceFeeValue,
          spendFunding: '',
        };
        setInvoiceBillingSnapshot(invoiceBillingDetails);
        setClientBillingInfo(invoiceBillingDetails);
        setLineItems(
          invoice.line_items?.length
            ? invoice.line_items.map((item) => ({
                id: item.id,
                line_item_type: item.line_item_type || 'custom',
                description: item.description || '',
                amount: formatAmountInput(item.amount ?? item.line_total ?? ''),
              }))
            : [createEmptyLineItem()]
        );
        markLineItemsDirty(false);
        setExistingAttachments(invoice.attachments || []);
        setDueDateDirty(true);
        if (invoice.client) {
          const label =
            invoice.client_name ||
            invoice.client_data?.name ||
            (typeof invoice.client === 'number' ||
            typeof invoice.client === 'string'
              ? String(invoice.client)
              : '');
          if (label) {
            addClientOption({ value: invoice.client, label });
          }
          apiClient
            .request({ url: `dashboard/clients/${invoice.client}/`, method: 'GET' })
            .then((clientRes) => {
              const spendFunding = clientRes?.data?.spend_funding ?? '';
              if (!spendFunding) {
                return;
              }
              setInvoiceBillingSnapshot((prev) => ({ ...prev, spendFunding }));
              setClientBillingInfo((prev) => ({ ...prev, spendFunding }));
            })
            .catch((err) => {
              console.error('Failed to load client spend funding', err);
            });
        }
      })
      .catch((err) => {
        console.error('Failed to load invoice', err);
        showErrorMessage('Unable to load invoice.');
        navigate(paths.dashboard.invoices.list);
      })
      .finally(() => setLoading(false));
  }, [addClientOption, invoiceId, isEdit, markLineItemsDirty, navigate, showErrorMessage]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const breakdownRequestRef = useRef(0);
  const {
    client: selectedClient,
    period_start: periodStart,
    period_end: periodEnd,
    billing_scope: billingScope,
  } = formData;
  const selectedIncludedClientIds = useMemo(
    () =>
      Array.isArray(formData.included_client_ids)
        ? formData.included_client_ids
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value))
        : [],
    [formData.included_client_ids]
  );

  const displayedBillingInfo = useMemo(
    () => (isEdit ? invoiceBillingSnapshot : clientBillingInfo),
    [clientBillingInfo, invoiceBillingSnapshot, isEdit]
  );

  const approvalResetToastRef = useRef(Boolean(formData.needs_amount_confirmation));

  const markApprovalRequired = useCallback(() => {
    if (!isEdit) {
      return;
    }
    setFormData((prev) =>
      prev.needs_amount_confirmation
        ? prev
        : (() => {
            if (!approvalResetToastRef.current) {
              showSuccessMessage('Changes made — approval reset.');
              approvalResetToastRef.current = true;
            }
            return { ...prev, needs_amount_confirmation: true };
          })()
    );
  }, [isEdit, showSuccessMessage]);

  const applyBreakdownToLineItems = useCallback(
    (entries, total, { periodStart: start, periodEnd: end } = {}) => {
      const nextLineItems = buildBreakdownLineItems({
        entries,
        total,
        periodStart: start,
        periodEnd: end,
        spendFunding: displayedBillingInfo.spendFunding,
        feePercentageValue: displayedBillingInfo.feePercentageValue,
      });
      setLineItems(nextLineItems.length ? nextLineItems : [createEmptyLineItem()]);
      markLineItemsDirty(false);
      markApprovalRequired();
    },
    [
      displayedBillingInfo.feePercentageValue,
      displayedBillingInfo.spendFunding,
      markApprovalRequired,
      markLineItemsDirty,
    ]
  );

  const fetchChannelBreakdown = useCallback(
    (
      clientId,
      periodStart,
      periodEnd,
      { autoApply = false, notifyOnError = false, includedClientIds = [] } = {}
    ) => {
      if (!clientId || !periodStart || !periodEnd) {
        breakdownRequestRef.current += 1;
        if (autoApply) {
          setLineItems([createEmptyLineItem()]);
          markLineItemsDirty(false);
        }
        return;
      }

      const requestId = breakdownRequestRef.current + 1;
      breakdownRequestRef.current = requestId;
      setBreakdownLoading(true);
      if (notifyOnError) {
        setRefreshingLineItems(true);
      }

      const request =
        billingScope === 'parent'
          ? getParentChannelBreakdown({
              client: clientId,
              period_start: periodStart,
              period_end: periodEnd,
              included_client_ids: includedClientIds,
            })
          : getClientChannelBreakdown({
              client: clientId,
              period_start: periodStart,
              period_end: periodEnd,
            });

      request
        .then((res) => {
          if (breakdownRequestRef.current !== requestId) {
            return;
          }
          const rawBreakdown = Array.isArray(res?.data?.breakdown) ? res.data.breakdown : [];
          const normalized = rawBreakdown.map((entry) => ({
            channel: entry?.channel || 'Unassigned',
            spend: Number(entry?.spend ?? 0) || 0,
          }));
          const total = Number(res?.data?.total_spend ?? 0) || 0;
          setChannelBreakdown(normalized);
          setChannelBreakdownTotal(total);
          if (autoApply && !lineItemsDirtyRef.current) {
            if (billingScope === 'parent') {
              const parentLineItems = Array.isArray(res?.data?.line_items) ? res.data.line_items : [];
              const mapped = parentLineItems
                .map((item) => ({
                  line_item_type: item?.line_item_type || 'custom',
                  description: item?.description || '',
                  amount: formatAmountInput(item?.amount ?? ''),
                }))
                .filter((item) => item.description || item.amount);
              setLineItems(mapped.length ? mapped : [createEmptyLineItem()]);
              markLineItemsDirty(false);
              markApprovalRequired();
            } else {
              applyBreakdownToLineItems(normalized, total, {
                periodStart,
                periodEnd,
              });
            }
          }
        })
        .catch((err) => {
          console.error(
            billingScope === 'parent'
              ? 'Failed to load parent channel breakdown'
              : 'Failed to load channel breakdown',
            err
          );
          if (notifyOnError) {
            showErrorMessage(
              billingScope === 'parent'
                ? 'Unable to load parent invoice defaults.'
                : 'Unable to load channel spend breakdown.'
            );
          }
        })
        .finally(() => {
          if (notifyOnError) {
            setRefreshingLineItems(false);
          }
          setBreakdownLoading(false);
        });
    },
    [
      applyBreakdownToLineItems,
      billingScope,
      markApprovalRequired,
      markLineItemsDirty,
      showErrorMessage,
    ]
  );

  useEffect(() => {
    if (!selectedClient || !periodStart || !periodEnd) {
      breakdownRequestRef.current += 1;
      return;
    }
    fetchChannelBreakdown(selectedClient, periodStart, periodEnd, {
      autoApply: !isEdit && !lineItemsDirty,
      includedClientIds: selectedIncludedClientIds,
    });
  }, [
    billingScope,
    fetchChannelBreakdown,
    isEdit,
    lineItemsDirty,
    selectedIncludedClientIds,
    periodEnd,
    periodStart,
    selectedClient,
  ]);

  useEffect(() => {
    if (billingScope === 'parent') {
      setMonthlyMinimumPreview({
        required: false,
        amount: 1000,
        feesToDate: 0,
        gap: 0,
      });
      return;
    }
    if (!selectedClient || !periodStart || !periodEnd) {
      setMonthlyMinimumPreview({
        required: false,
        amount: 1000,
        feesToDate: 0,
        gap: 0,
      });
      return;
    }

    getClientSpendPreview({
      client: selectedClient,
      period_start: periodStart,
      period_end: periodEnd,
    })
      .then((res) => {
        const data = res?.data || {};
        setMonthlyMinimumPreview({
          required: Boolean(data.monthly_minimum_required),
          amount: Number(data.monthly_minimum_amount ?? 1000) || 1000,
          feesToDate: Number(data.monthly_media_fees_to_date ?? 0) || 0,
          gap: Number(data.monthly_media_fee_gap ?? 0) || 0,
        });
      })
      .catch((err) => {
        console.error('Failed to load monthly minimum preview', err);
        setMonthlyMinimumPreview({
          required: false,
          amount: 1000,
          feesToDate: 0,
          gap: 0,
        });
      });
  }, [billingScope, periodEnd, periodStart, selectedClient]);

  useEffect(() => {
    if (billingScope !== 'parent') {
      setParentClientOptions([]);
      setParentCompanyInfo({ id: null, name: '' });
      return;
    }
    if (!selectedClient) {
      setParentClientOptions([]);
      setParentCompanyInfo({ id: null, name: '' });
      return;
    }

    let isCancelled = false;
    apiClient
      .request({ url: `dashboard/clients/${selectedClient}/`, method: 'GET' })
      .then((res) => {
        if (isCancelled) {
          return null;
        }
        const detail = res?.data || {};
        const parentId = Number(detail.parent_company || 0) || null;
        const parentName = detail.parent_company_name || '';
        if (!parentId) {
          setParentClientOptions([]);
          setParentCompanyInfo({ id: null, name: '' });
          return null;
        }
        setParentCompanyInfo({ id: parentId, name: parentName });
        return apiClient.request({
          url: 'dashboard/clients/',
          method: 'GET',
          payload: {
            fields: 'id,name,parent_company,parent_company_name',
            page_size: 500,
          },
        });
      })
      .then((clientsRes) => {
        if (isCancelled || !clientsRes) {
          return;
        }
        const rawRows = Array.isArray(clientsRes?.data?.results)
          ? clientsRes.data.results
          : Array.isArray(clientsRes?.data)
            ? clientsRes.data
            : [];
        const selectedRow = rawRows.find((row) => Number(row.id) === Number(selectedClient));
        const targetParent = Number(selectedRow?.parent_company || 0);
        if (!targetParent) {
          setParentClientOptions([]);
          return;
        }
        const options = rawRows
          .filter((row) => Number(row?.parent_company) === targetParent)
          .map((row) => ({ value: Number(row.id), label: row.name || `Client ${row.id}` }))
          .filter((row) => Number.isFinite(row.value));
        setParentClientOptions(options);
        setFormData((prev) => {
          const existing = Array.isArray(prev.included_client_ids) ? prev.included_client_ids : [];
          if (existing.length) {
            return prev;
          }
          const allIds = options.map((entry) => entry.value);
          return {
            ...prev,
            included_client_ids:
              allIds.length > 0
                ? allIds
                : Number(selectedClient)
                  ? [Number(selectedClient)]
                  : [],
          };
        });
      })
      .catch((err) => {
        console.error('Failed to load parent client options', err);
        if (!isCancelled) {
          setParentClientOptions([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [billingScope, selectedClient]);

  useEffect(() => {
    if (billingScope === 'parent') {
      return;
    }
    if (isEdit || lineItemsDirty) {
      return;
    }
    if (!channelBreakdown.length) {
      return;
    }
    applyBreakdownToLineItems(channelBreakdown, channelBreakdownTotal, {
      periodStart,
      periodEnd,
    });
  }, [
    applyBreakdownToLineItems,
    billingScope,
    channelBreakdown,
    channelBreakdownTotal,
    isEdit,
    lineItemsDirty,
    periodEnd,
    periodStart,
  ]);

  useEffect(() => {
    if (billingScope !== 'parent') {
      return;
    }
    setLineItems([createEmptyLineItem()]);
    markLineItemsDirty(false);
  }, [billingScope, markLineItemsDirty]);

  useEffect(() => {
    if (!lineItemsDirty) {
      return;
    }
    setLineItems((prev) =>
      recalculateMediaSpendFeeLineItems(prev, displayedBillingInfo.feePercentageValue, {
        spendFunding: displayedBillingInfo.spendFunding,
        totalSpend: billingScope === 'parent' ? 0 : channelBreakdownTotal,
      })
    );
  }, [
    billingScope,
    channelBreakdownTotal,
    displayedBillingInfo.feePercentageValue,
    displayedBillingInfo.spendFunding,
    lineItemsDirty,
  ]);

  const prefillClientDefaults = useCallback(
    (clientId) => {
      if (!clientId) {
        setClientBillingInfo({
          billingCycle: '',
          billingTerms: '',
          feePercentageDisplay: '',
          feePercentageValue: 0,
          spendFunding: '',
        });
        return;
      }

      setClientBillingInfo({
        billingCycle: '',
        billingTerms: '',
        feePercentageDisplay: '',
        feePercentageValue: 0,
        spendFunding: '',
      });
      markLineItemsDirty(false);

      apiClient
        .request({ url: `dashboard/clients/${clientId}/`, method: 'GET' })
        .then((res) => {
          const data = res.data || {};
          const feeValue = toFeePercentageNumber(data.fees, { isFraction: true });
          const billingCycleKey = resolveBillingCycleKey(
            data.billing_cycle ?? data.billing_cycle_display
          );
          const period = getPeriodForBillingCycle(billingCycleKey);
          setClientBillingInfo({
            billingCycle: data.billing_cycle_display ?? data.billing_cycle ?? '',
            billingTerms: data.billing_terms_display ?? data.billing_terms ?? '',
            feePercentageDisplay: formatFeePercentageDisplay(feeValue),
            feePercentageValue: feeValue,
            spendFunding: data.spend_funding ?? '',
          });

          if (data.name) {
            addClientOption({ value: data.id ?? clientId, label: data.name });
          }

          setFormData((prev) => ({
            ...prev,
            period_start: period.start,
            period_end: period.end,
            ...(dueDateDirty
              ? {}
              : {
                  due_date:
                    calculateDueDate(prev.issue_date, data.billing_terms_display ?? data.billing_terms) ||
                    prev.due_date,
                }),
          }));
          markLineItemsDirty(false);
          if (billingScope !== 'parent' && clientId && period.start && period.end) {
            fetchChannelBreakdown(clientId, period.start, period.end, {
              autoApply: true,
            });
          }
        })
        .catch((err) => {
          console.error('Failed to fetch client billing defaults', err);
          showErrorMessage('Unable to load client billing defaults.');
          setClientBillingInfo({
            billingCycle: '',
            billingTerms: '',
            feePercentageDisplay: '',
            feePercentageValue: 0,
            spendFunding: '',
          });
        });
    },
    [addClientOption, billingScope, fetchChannelBreakdown, markLineItemsDirty, showErrorMessage]
  );

  const handleRefreshLineItems = useCallback(() => {
    if (!selectedClient || !periodStart || !periodEnd) {
      showErrorMessage('Select a client and billing period before refreshing defaults.');
      return;
    }
    markLineItemsDirty(false);
    fetchChannelBreakdown(selectedClient, periodStart, periodEnd, {
      autoApply: true,
      notifyOnError: true,
      includedClientIds: selectedIncludedClientIds,
    });
  }, [
    billingScope,
    fetchChannelBreakdown,
    markLineItemsDirty,
    periodEnd,
    periodStart,
    selectedIncludedClientIds,
    showErrorMessage,
    selectedClient,
  ]);

  const handleFieldChange = (field) => (eventOrValue) => {
    const isEvent = eventOrValue && typeof eventOrValue === 'object' && 'target' in eventOrValue;
    const target = isEvent ? eventOrValue.target : { value: eventOrValue };
    const { value } = target;
    const type = target.type;
    const checked = target.checked;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => {
      if (field === 'billing_scope') {
        return {
          ...prev,
          billing_scope: nextValue,
          included_client_ids: [],
        };
      }
      return {
        ...prev,
        [field]: nextValue,
      };
    });
    if (field !== 'needs_amount_confirmation') {
      markApprovalRequired();
    }
    if (['client', 'period_start', 'period_end'].includes(field)) {
      markLineItemsDirty(false);
    }
    if (field === 'issue_date' && !dueDateDirty && nextValue) {
      const nextDue =
        calculateDueDate(nextValue, displayedBillingInfo.billingTerms) ||
        dayjs(nextValue).add(30, 'day').format('YYYY-MM-DD');
      setFormData((prev) => ({ ...prev, due_date: nextDue }));
    }
    if (field === 'due_date') {
      setDueDateDirty(true);
    }
  };

  const handlePeriodStartChange = (event) => {
    handleFieldChange('period_start')(event);
    const nextPeriodStart = event.target.value;
    const nextPeriodEnd = formData.period_end;

    if (billingScope !== 'parent' && selectedClient && nextPeriodStart && nextPeriodEnd) {
      markLineItemsDirty(false);
      fetchChannelBreakdown(selectedClient, nextPeriodStart, nextPeriodEnd, { autoApply: true });
    }
  };

  const handlePeriodEndChange = (event) => {
    handleFieldChange('period_end')(event);
    const nextPeriodEnd = event.target.value;
    const nextPeriodStart = formData.period_start;

    if (billingScope !== 'parent' && selectedClient && nextPeriodStart && nextPeriodEnd) {
      markLineItemsDirty(false);
      fetchChannelBreakdown(selectedClient, nextPeriodStart, nextPeriodEnd, { autoApply: true });
    }
  };

  const handleLineItemChange = (index, key, value) => {
    markApprovalRequired();
    markLineItemsDirty(true);
    setLineItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [key]: value };
      return recalculateMediaSpendFeeLineItems(copy, displayedBillingInfo.feePercentageValue, {
        spendFunding: displayedBillingInfo.spendFunding,
        totalSpend: channelBreakdownTotal,
      });
    });
  };

  const addLineItem = () => {
    markApprovalRequired();
    setLineItems((prev) =>
      recalculateMediaSpendFeeLineItems(
        [...prev.filter((item) => !isMediaSpendFeeItem(item)), createEmptyLineItem()],
        displayedBillingInfo.feePercentageValue,
        {
          spendFunding: displayedBillingInfo.spendFunding,
          totalSpend: channelBreakdownTotal,
        }
      )
    );
    markLineItemsDirty(true);
  };

  const hasMonthlyTopUpLineItem = useMemo(
    () => lineItems.some((item) => isMonthlyTopUpItem(item)),
    [lineItems]
  );

  const currentPeriodMediaFeeAmount = useMemo(() => {
    const mediaFeeItem = lineItems.find((item) => isMediaSpendFeeItem(item));
    const value = Number(mediaFeeItem?.amount ?? 0);
    return Number.isFinite(value) ? value : 0;
  }, [lineItems]);

  const suggestedMonthlyTopUpAmount = useMemo(
    () => Math.max(0, monthlyMinimumPreview.gap - currentPeriodMediaFeeAmount),
    [currentPeriodMediaFeeAmount, monthlyMinimumPreview.gap]
  );

  const handleAddMonthlyTopUp = () => {
    if (!monthlyMinimumPreview.required || suggestedMonthlyTopUpAmount <= 0) {
      return;
    }
    if (hasMonthlyTopUpLineItem) {
      showErrorMessage('Monthly minimum top-up line already exists.');
      return;
    }
    markApprovalRequired();
    setLineItems((prev) =>
      recalculateMediaSpendFeeLineItems(
        [
          ...prev.filter((item) => !(item?.description === '' && !item?.amount)),
          {
            line_item_type: 'monthly_min_top_up',
            description: MONTHLY_MIN_TOP_UP_LABEL,
            amount: formatAmountInput(suggestedMonthlyTopUpAmount),
          },
        ],
        displayedBillingInfo.feePercentageValue,
        {
          spendFunding: displayedBillingInfo.spendFunding,
          totalSpend: channelBreakdownTotal,
        }
      )
    );
    markLineItemsDirty(true);
  };

  const removeLineItem = (index) => {
    markApprovalRequired();
    setLineItems((prev) =>
      recalculateMediaSpendFeeLineItems(
        prev.filter((_, idx) => idx !== index),
        displayedBillingInfo.feePercentageValue,
        {
          spendFunding: displayedBillingInfo.spendFunding,
          totalSpend: channelBreakdownTotal,
        }
      )
    );
    markLineItemsDirty(true);
  };

  const handleAttachmentSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }
    markApprovalRequired();
    setPendingAttachments((prev) => [
      ...prev,
      ...files.map((file, index) => ({ id: `${file.name}-${Date.now()}-${index}`, file, notes: '' })),
    ]);
    event.target.value = '';
  };

  const handleAttachmentNotesChange = (id, value) => {
    markApprovalRequired();
    setPendingAttachments((prev) => prev.map((item) => (item.id === id ? { ...item, notes: value } : item)));
  };

  const removePendingAttachment = (id) => {
    markApprovalRequired();
    setPendingAttachments((prev) => prev.filter((item) => item.id !== id));
  };

  const subtotal = useMemo(
    () =>
      lineItems.reduce((sum, item) => {
        const numeric = Number(item.amount || 0);
        return sum + (Number.isNaN(numeric) ? 0 : numeric);
      }, 0),
    [lineItems]
  );
  const invoiceTotal = subtotal;
  const amountMissing = subtotal <= 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.client) {
      showErrorMessage('Select a client before saving.');
      return;
    }
    setSaving(true);
    try {
      let preparedLineItems = [];
      let hasMissingDescription = false;

      lineItems.forEach((item, index) => {
        const description = String(item?.description || '').trim();
        const amountValue = parseAmount(item.amount);
        const hasAmountInput = ![null, undefined, ''].includes(item?.amount);

        if (!description) {
          const isPlaceholder = !item?.id && !hasAmountInput;
          if (!isPlaceholder) {
            hasMissingDescription = true;
          }
          return;
        }

        preparedLineItems.push({
          id: item.id,
          line_item_type: item.line_item_type || 'custom',
          description,
          amount: amountValue ?? 0,
          position: index,
        });
      });

      if (hasMissingDescription) {
        showErrorMessage('Each non-empty line item must include a description.');
        return;
      }

      if (
        preparedLineItems.length === 0 &&
        formData.billing_scope !== 'parent' &&
        !lineItemsDirty &&
        formData.client &&
        formData.period_start &&
        formData.period_end
      ) {
        setRefreshingLineItems(true);
        try {
          const res = await getClientChannelBreakdown({
            client: formData.client,
            period_start: formData.period_start,
            period_end: formData.period_end,
          });
          const rawBreakdown = Array.isArray(res?.data?.breakdown) ? res.data.breakdown : [];
          const normalized = rawBreakdown.map((entry) => ({
            channel: entry?.channel || 'Unassigned',
            spend: Number(entry?.spend ?? 0) || 0,
          }));
          const total = Number(res?.data?.total_spend ?? 0) || 0;
          const refreshedItems = buildBreakdownLineItems({
            entries: normalized,
            total,
            periodStart: formData.period_start,
            periodEnd: formData.period_end,
            spendFunding: displayedBillingInfo.spendFunding,
            feePercentageValue: displayedBillingInfo.feePercentageValue,
          });
          if (refreshedItems.length) {
            setLineItems(refreshedItems);
            markLineItemsDirty(false);
            preparedLineItems = refreshedItems.map((item, index) => ({
              line_item_type: item.line_item_type || 'custom',
              description: item.description,
              amount: parseAmount(item.amount) ?? 0,
              position: index,
            }));
          }
        } catch (refreshError) {
          console.error('Failed to refresh line items before save', refreshError);
          showErrorMessage('Unable to refresh line items. Please try saving again.');
          return;
        } finally {
          setRefreshingLineItems(false);
        }
      }

      const payload = {
        billing_scope: formData.billing_scope || 'client',
        client: formData.client,
        ...(formData.billing_scope === 'parent'
          ? { included_client_ids: formData.included_client_ids || [] }
          : {}),
        period_start: formData.period_start || null,
        period_end: formData.period_end || null,
        issue_date: formData.issue_date || null,
        due_date: formData.due_date || null,
        notes: formData.notes,
        external_notes: formData.external_notes,
        needs_amount_confirmation: formData.needs_amount_confirmation,
        line_items: preparedLineItems,
      };
      let response;
      if (isEdit) {
        response = await updateInvoice(invoiceId, payload);
      } else {
        response = await createInvoice(payload);
      }
      const savedInvoice = response.data;

      if (pendingAttachments.length) {
        for (const attachment of pendingAttachments) {
          const attachmentForm = new FormData();
          attachmentForm.append('file', attachment.file);
          if (attachment.notes) {
            attachmentForm.append('notes', attachment.notes);
          }
          attachmentForm.append('original_filename', attachment.file.name);
          try {
            await uploadInvoiceAttachment(savedInvoice.id, attachmentForm);
          } catch (error) {
            console.error('Failed to upload attachment', error);
            showErrorMessage(`Failed to upload ${attachment.file.name}`);
          }
        }
        showSuccessMessage('Attachments uploaded.');
        setPendingAttachments([]);
      }

      if (sendAfterSave) {
        if (savedInvoice.needs_amount_confirmation) {
          showErrorMessage('Approve the invoice before sending.');
          navigate(paths.dashboard.invoices.details(savedInvoice.id));
          return;
        }
        try {
          const sendResponse = await sendInvoice(savedInvoice.id, { message: sendNote });
          showSuccessMessage('Invoice sent and payment link generated.');
          navigate(paths.dashboard.invoices.details(sendResponse.data.id));
          return;
        } catch (error) {
          console.error('Failed to send invoice after save', error);
          showErrorMessage('Invoice saved but sending failed. Review the invoice details.');
        }
      }

      showSuccessMessage('Invoice saved successfully.');
      navigate(paths.dashboard.invoices.details(savedInvoice.id));
    } catch (error) {
      console.error('Failed to save invoice', error);
      const detailMessage = getInvoiceSaveErrorMessage(error);
      showErrorMessage(detailMessage || 'Unable to save invoice. Please verify the form inputs.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} p={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Stack spacing={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(paths.dashboard.invoices.list)}>
            Back to invoices
          </Button>
          <Typography variant="h4">{isEdit ? 'Edit invoice' : 'Create invoice'}</Typography>
          <Typography color="text.secondary">Fill in billing period, spend, and notes before sending.</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() =>
              setFormData((prev) => {
                approvalResetToastRef.current = false;
                return { ...prev, needs_amount_confirmation: false };
              })
            }
            disabled={!formData.needs_amount_confirmation}
          >
            {formData.needs_amount_confirmation ? 'Approve' : 'Approved'}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            loading={saving}
          >
            Save invoice
          </LoadingButton>
        </Stack>
      </Stack>

      {amountMissing && formData.billing_scope !== 'parent' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Line items total zero. Approval is still required before sending.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader title="Invoice details" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Invoice scope"
                    value={formData.billing_scope || 'client'}
                    onChange={handleFieldChange('billing_scope')}
                    fullWidth
                    disabled={isEdit}
                  >
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="parent">Parent company</MenuItem>
                  </TextField>
                </Grid>
                {formData.billing_scope === 'parent' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Parent company"
                      value={parentCompanyInfo.name || 'Select billing client'}
                      fullWidth
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={clientOptions}
                    getOptionLabel={(option) => option?.label ?? ''}
                    value={selectedClientOption}
                    onChange={(event, option) => {
                      const clientId = option?.value ?? '';
                      handleFieldChange('client')(clientId);
                      if (formData.billing_scope === 'parent') {
                        setFormData((prev) => ({ ...prev, included_client_ids: [] }));
                      }
                      prefillClientDefaults(clientId);
                    }}
                    onInputChange={handleClientSearch}
                    loading={clientsLoading}
                    disabled={isEdit}
                    fullWidth
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          formData.billing_scope === 'parent'
                            ? 'Billing client'
                            : 'Client'
                        }
                        required
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {clientsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                {formData.billing_scope === 'parent' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={parentClientOptions}
                      getOptionLabel={(option) => option?.label ?? ''}
                      value={parentClientOptions.filter((option) =>
                        (formData.included_client_ids || []).includes(option.value)
                      )}
                      onChange={(event, options) => {
                        const nextIds = options.map((option) => option.value);
                        setFormData((prev) => ({ ...prev, included_client_ids: nextIds }));
                        markApprovalRequired();
                      }}
                      fullWidth
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Included clients"
                          helperText="Parent-company invoice will aggregate these clients."
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="date"
                    label="Period start"
                    value={formData.period_start}
                    onChange={handlePeriodStartChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="date"
                    label="Period end"
                    value={formData.period_end}
                    onChange={handlePeriodEndChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="date"
                    label="Issue date"
                    value={formData.issue_date}
                    onChange={handleFieldChange('issue_date')}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="date"
                    label="Due date"
                    value={formData.due_date}
                    onChange={handleFieldChange('due_date')}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Fee %"
                    value={displayedBillingInfo.feePercentageDisplay || '—'}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing cycle"
                    value={displayedBillingInfo.billingCycle || '—'}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing terms"
                    value={displayedBillingInfo.billingTerms || '—'}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Ad spend funding"
                    value={formatSpendFundingLabel(displayedBillingInfo.spendFunding)}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="ACH instructions"
                    value={achInstructions}
                    fullWidth
                    multiline
                    minRows={2}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Client-visible notes"
                    value={formData.external_notes}
                    onChange={handleFieldChange('external_notes')}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Shown to clients on emails and PDFs."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Internal notes"
                    value={formData.notes}
                    onChange={handleFieldChange('notes')}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardHeader title="Send options" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendAfterSave}
                      onChange={(event) => setSendAfterSave(event.target.checked)}
                      disabled={formData.needs_amount_confirmation}
                    />
                  }
                  label="Send invoice immediately after saving"
                />
                {formData.needs_amount_confirmation && (
                  <Typography variant="caption" color="text.secondary">
                    Approve the invoice before sending.
                  </Typography>
                )}
                {sendAfterSave && (
                  <TextField
                    label="Send message"
                    value={sendNote}
                    onChange={(event) => setSendNote(event.target.value)}
                    multiline
                    minRows={3}
                    placeholder="Optional message for the invoice email."
                  />
                )}
                <Alert severity="info">
                  When sending, the platform will generate a PDF, attach uploaded files, and create a Stripe payment link.
                </Alert>
              </Stack>
            </CardContent>
          </Card>
          <Box mt={3}>
            <Card>
              <CardHeader title="Attachments" />
              <Divider />
              <CardContent>
                <Stack spacing={2}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                  >
                    Add attachment
                    <input type="file" hidden multiple onChange={handleAttachmentSelect} />
                  </Button>
                  {pendingAttachments.length ? (
                    <Stack spacing={1}>
                      {pendingAttachments.map((attachment) => (
                        <Stack direction="row" spacing={2} alignItems="center" key={attachment.id}>
                          <Chip label={attachment.file.name} />
                          <TextField
                            label="Notes"
                            value={attachment.notes}
                            onChange={(event) => handleAttachmentNotesChange(attachment.id, event.target.value)}
                            size="small"
                          />
                          <IconButton color="error" onClick={() => removePendingAttachment(attachment.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No new attachments selected.</Typography>
                  )}
                  {existingAttachments.length > 0 && (
                    <Stack spacing={1}>
                      <Alert severity="info">
                        {existingAttachments.length} attachment(s) already linked to this invoice.
                      </Alert>
                      <Stack spacing={0.5}>
                        {existingAttachments.map((attachment) => (
                          <Typography key={attachment.id} variant="body2" color="text.secondary">
                            {attachment.original_filename || 'Attachment'} • Uploaded {formatDateTime(attachment.uploaded_at)}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Card>
          <CardHeader
            title="Line items"
            action={
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <LoadingButton
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshLineItems}
                  loading={refreshingLineItems}
                  disabled={
                    refreshingLineItems ||
                    !formData.client ||
                    !formData.period_start ||
                    !formData.period_end ||
                    breakdownLoading
                  }
                >
                  Refresh defaults
                </LoadingButton>
                <Button startIcon={<AddIcon />} onClick={addLineItem}>
                  Add line item
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleAddMonthlyTopUp}
                  disabled={
                    !monthlyMinimumPreview.required ||
                    suggestedMonthlyTopUpAmount <= 0 ||
                    hasMonthlyTopUpLineItem
                  }
                >
                  Add monthly top-up
                </Button>
              </Stack>
            }
          />
          <Divider />
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell>
                      <TextField
                        value={item.description}
                        onChange={(event) => handleLineItemChange(index, 'description', event.target.value)}
                        placeholder="Description"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 160 }}>
                      <TextField
                        type="number"
                        value={item.amount}
                        onChange={(event) => handleLineItemChange(index, 'amount', event.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                        size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => removeLineItem(index)} disabled={lineItems.length === 1}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Line items total</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2">{formatCurrency(subtotal)}</Typography>
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableBody>
            </Table>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Chip label={`Line items total: ${formatCurrency(subtotal)}`} color="primary" />
                <Chip label={`Invoice total: ${formatCurrency(invoiceTotal)}`} color="success" />
                <Chip
                  label={`Client fee rate: ${displayedBillingInfo.feePercentageDisplay || '—'}`}
                  variant="outlined"
                />
              </Stack>
            </Stack>
            <Box mt={2}>
              {monthlyMinimumPreview.required ? (
                <Alert severity={suggestedMonthlyTopUpAmount > 0 ? 'warning' : 'success'}>
                  Monthly minimum: {formatCurrency(monthlyMinimumPreview.amount)}. Fees invoiced this
                  month: {formatCurrency(monthlyMinimumPreview.feesToDate)}. Remaining gap:{' '}
                  {formatCurrency(monthlyMinimumPreview.gap)}. Current period media fee:{' '}
                  {formatCurrency(currentPeriodMediaFeeAmount)}. Suggested top-up:{' '}
                  {formatCurrency(suggestedMonthlyTopUpAmount)}.
                </Alert>
              ) : (
                <Alert severity="info">This client is exempt from the monthly minimum.</Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
